import React, {useRef, useEffect, useState, useMemo} from 'react';
import PropTypes from 'prop-types';
import SimpleBar from 'simplebar-react';
import EditorSidebarContainer from './EditorSidebarContainer';
import EditorSidebarFolder from './EditorSidebarFolder';
import EditorSidebarTab from './EditorSidebarTab';
import SMCTextInput from '../../Simple/SMCTextInput';
import RowLayout from '../../Layouts/RowLayout';
import SMCIcon from '../../Simple/SMCIcon';

function isTabMatchesFilterText(tab, filterText) {
    // If there is no text, all matches
    if (!filterText) {
        return true;
    }
    if (tab.title.toLowerCase().includes(filterText.toLowerCase())) {
        return true;
    }

    // If folder has a child that matches, it matches also
    const isFolder = !!tab.children;
    if (isFolder) {
        for (let i = 0; i < tab.children.length; i++) {
            const innerTab = tab.children[i];
            if (innerTab.title.toLowerCase().includes(filterText.toLowerCase())) {
                return true;
            }
        }
    }

    return false;
}

function getErrorsCount(errorList) {
    let errorCount = 0;
    if (errorList && errorList.length) {
        errorList.forEach(errorObject => {
            // Only add page errors
            if (errorObject.pageId) {
                errorCount += errorObject.messages.length;
            }
        });
    }
    return errorCount;
}

function EditorSidebar({
    tabs,
    onSelectPage,
    height,
    width,
    selectedTabId,
    errorListByPage
}) {
    const isFirstRenderRef = useRef(true);

    useEffect(() => {
        isFirstRenderRef.current = false;
    }, []);
    
    const [filterText, setFilterText] = useState('');

    const pageErrorsCount = useMemo(() => getErrorsCount(errorListByPage), [errorListByPage]);

    const indexOfLastTab = useMemo(() => {
        let lastIndex = 0;
        if (tabs && tabs.length) {
            tabs.forEach((tab, currentTabIndex) => {
                let isTabVisible;
                if (tab.hide) {
                    isTabVisible = false;
                }
                else if (filterText) {
                    isTabVisible = isTabMatchesFilterText(tab, filterText);
                } else {
                    isTabVisible = true;
                }
                if (isTabVisible) {
                    lastIndex = currentTabIndex;
                }
            });
        }
        return lastIndex;
    }, [tabs, filterText]);

    return (
        <EditorSidebarContainer width={width} height={height}>
            <div style={{ borderBottom: '1px solid #0000004d', marginBottom: '8px', height: 37 }}>
                <RowLayout spacing="10px" style={{ marginLeft: '8px', marginBottom: '8px' }}>
                    <SMCTextInput
                        width={pageErrorsCount > 0 ? 150 : 198}
                        placeholder="Search"
                        allowClearAll
                        value={filterText}
                        onChange={e => setFilterText(e.target.value)}
                        suffixIcon="search"
                        ignoreDisabledContext
                    />
                    {
                        pageErrorsCount > 0 && (
                            <span style={{ borderLeft: '1px solid #0000004d', height: 24 }} />
                        )
                    }
                    {
                        pageErrorsCount > 0 && (
                            <>
                            <span style={{ fontSize: '15px', marginRight: '3px' }}>
                                {pageErrorsCount}
                            </span>
                            <SMCIcon icon="warning" />
                            </>
                        )
                    }
                </RowLayout>
            </div>
            <div id="editorTabsContainer">
                <SimpleBar hidden={false} autoHide={false} style={{ height: height - 56 }}>
                    {
                        tabs.map((tab, index) => {
                            if (tab.hide) {
                                return undefined;
                            }
                            const showTab = isTabMatchesFilterText(tab, filterText);
                            const isFolder = !!tab.children;
                            
                            if (isFolder) {
                                let folderDefaultOpen = false;
                                let folderHasError = false;
                                let folderChildrenWithError;

                                if (isFirstRenderRef) {
                                    const isChildSelected = tab.children.filter(innerTab => innerTab.id === selectedTabId).length === 1;
                                    if (isChildSelected) {
                                        folderDefaultOpen = true;
                                    }
                                }
                                if (errorListByPage) {
                                    const errorPagesNames = errorListByPage
                                                                .filter(errorObject => tab.children.find(innerTab => innerTab.id === errorObject.pageId))
                                                                .map(errorObject => errorObject.pageName);
                                    if (errorPagesNames.length) {
                                        folderHasError = true;
                                        folderChildrenWithError = errorPagesNames;
                                    }
                                }
                                return (
                                    <EditorSidebarFolder
                                        first={index === 0}
                                        last={index === indexOfLastTab}
                                        forceOpen={Boolean(filterText)}
                                        hide={!showTab}
                                        highlightText={filterText}
                                        icon={tab.icon}
                                        title={tab.title}
                                        defaultOpen={folderDefaultOpen}
                                        key={tab.id || index}
                                        hasError={folderHasError}
                                        childrenWithError={folderChildrenWithError}
                                    >
                                        {
                                            tab.children.map((innerTab, index) => {
                                                let showTab;
                                                if (!filterText) {
                                                    showTab = true;
                                                } else if (tab.title.toLowerCase().includes(filterText.toLowerCase())) {
                                                    // If there is filter text, and the parent folder matches it, all children must be shown
                                                    showTab = true;
                                                } else {
                                                    showTab = isTabMatchesFilterText(innerTab, filterText);
                                                }

                                                if (!showTab) {
                                                    return undefined;
                                                }
                        
                                                let pageHasErrorMessages = false;
                                                let pageErrorMessages;
                                                if (innerTab.id && errorListByPage) {
                                                    const pageErrorObject = errorListByPage.find(errorObject => errorObject.pageId === innerTab.id);
                                                    if (pageErrorObject) {
                                                        pageHasErrorMessages = true;
                                                        pageErrorMessages = pageErrorObject.messages;
                                                    }
                                                }
                                                return (
                                                    <EditorSidebarTab
                                                        highlightText={filterText}
                                                        onSelectPage={onSelectPage}
                                                        selectedId={selectedTabId}
                                                        id={innerTab.id || index}
                                                        title={innerTab.title}
                                                        icon={innerTab.icon}
                                                        extraIcon={innerTab.extraIcon}
                                                        key={innerTab.id || index}
                                                        hasError={pageHasErrorMessages}
                                                        errorMessages={pageErrorMessages}
                                                    />
                                                )
                                            })
                                        }
                                    </EditorSidebarFolder>
                                );
                            }
                            else {
                                let pageHasErrorMessages = false;
                                let pageErrorMessages;
                                if (tab.id && errorListByPage) {
                                    const pageErrorObject = errorListByPage.find(errorObject => errorObject.pageId === tab.id);
                                    if (pageErrorObject) {
                                        pageHasErrorMessages = true;
                                        pageErrorMessages = pageErrorObject.messages;
                                    }
                                }
                                return (
                                    <EditorSidebarTab
                                        first={index === 0}
                                        last={index === indexOfLastTab}
                                        hide={!showTab}
                                        highlightText={filterText}
                                        onSelectPage={onSelectPage}
                                        selectedId={selectedTabId}
                                        id={tab.id || index}
                                        title={tab.title}
                                        icon={tab.icon}
                                        extraIcon={tab.extraIcon}
                                        key={tab.id || index}
                                        hasError={pageHasErrorMessages}
                                        errorMessages={pageErrorMessages}
                                    />
                                );
                            }
                        })
                    }
                </SimpleBar>
            </div>
        </EditorSidebarContainer>
    );
}

EditorSidebar.propTypes = {
    tabs: PropTypes.array.isRequired,
    onSelectPage: PropTypes.func.isRequired,
    height: PropTypes.number,
    selectedTabId: PropTypes.string,
}

export default EditorSidebar;